import { apiSlice } from "../../../features/api/apiSlice";

/**
 * The secretariat's side of the Graduate Pledge.
 *
 * Only the admin endpoints live here. The student's form deliberately does NOT
 * go through this slice: the slice attaches the stored bearer token and
 * force-redirects to /login on any 401, and neither belongs on a page whose
 * whole point is working without a session. PublicGraduatePledgeFormPage uses
 * a plain `fetch`, the same way the event card-verify page does.
 */

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

/* ── Types ──────────────────────────────────────────────────────────────── */

export type Programme = "bsc_btech" | "hnd";

/** Yes / No / not looked at yet. Null is a real state, not a missing value. */
export type TriState = boolean | null;

export type PledgeCohort = {
  id: number;
  name: string;
  year: number;
  ceremony_date: string | null;
  closes_on: string;
  closes_at?: string;
  is_open: boolean;
  /** Open AND inside the cut-off — what a student would actually find. */
  accepting: boolean;
  closed_reason: string | null;
  last_issued_no: number;
  next_form_no: string;
  submissions_count: number | null;
  declaration_text: string;
  consent_text: string;
  institutions: { id: number; name: string; acronym: string | null }[];
  created_at: string | null;
};

export type SignedFormState = {
  uploaded: boolean;
  status: "pending" | "accepted" | "rejected" | null;
  file_name: string | null;
  mime: string | null;
  size: number | null;
  uploaded_at: string | null;
  notes: string | null;
  attempts: number;
  can_upload: boolean;
  closed_reason: string | null;
};

export type PledgeSubmission = {
  id: number;
  form_no: string;
  surname: string;
  other_names: string;
  name: string;
  institution: string;
  institution_id: number;
  matric_no: string;
  programme: Programme;
  programme_label: string;
  graduation: string;
  graduation_month: number;
  graduation_year: number;
  student_membership_no: string | null;
  not_yet_member: boolean;
  email: string;
  phone: string;
  alt_phone: string | null;
  state_after_graduation: string;
  interest_area: string;
  interest_label: string;
  attended: TriState;
  graduation_confirmed: TriState;
  membership_no_issued: string | null;
  admin_notes: string | null;
  submitted_at: string | null;
  pdf_url: string;
  /** The signed printout the student sent back, and the verdict on it. */
  signed_form: SignedFormState;
  /** Only on the single-record read. */
  pdf_sent_count?: number;
  pdf_last_sent_at?: string | null;
  student_pdf_url?: string;
  updated_by?: string | null;
  admin_updated_at?: string | null;
};

export type PledgeFilters = {
  cohort_id?: number | string;
  q?: string;
  institution_id?: number | string;
  programme?: string;
  interest_area?: string;
  attended?: "yes" | "no" | "pending" | "";
  graduation_confirmed?: "yes" | "no" | "pending" | "";
  membership_issued?: "yes" | "no" | "";
  /** 'missing' is a NULL status — the students who still owe one. */
  signed_form?: "missing" | "pending" | "accepted" | "rejected" | "";
  page?: number;
  per_page?: number;
};

export type PledgeListMeta = {
  cohort?: {
    id: number; name: string; year: number;
    ceremony_date: string | null; closes_on: string;
    is_open: boolean; accepting: boolean; public_url: string;
  };
  cohorts: { id: number; name: string; year: number; is_open: boolean }[];
  by_institution: {
    id: number; name: string; acronym: string | null; total: number;
    /** Off this cohort's pilot list, but still holding submissions. */
    retired?: boolean;
  }[];
  by_programme: { value: string; label: string; total: number }[];
  totals: {
    submissions: number; attended: number; graduation_confirmed: number;
    membership_issued: number; not_yet_member: number;
    signed_pending: number; signed_accepted: number;
    signed_rejected: number; signed_missing: number;
  };
  filtered_total: number;
  options?: {
    institutions: { id: number; name: string; acronym: string | null }[];
    programmes: Record<string, string>;
    interest_areas: Record<string, string>;
  };
  message?: string;
};

/** Drop the empty strings, so a cleared filter leaves the query string. */
export function pledgeParams(filters: PledgeFilters): Record<string, any> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  );
}

/* ── Endpoints ──────────────────────────────────────────────────────────── */

export const graduatePledgeApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({

    getPledgeSubmissions: b.query<
      { data: PledgeSubmission[]; meta: PledgeListMeta; pagination: any },
      PledgeFilters | void
    >({
      query: (filters) => ({
        url: "/admin/graduate-pledge/submissions",
        params: pledgeParams((filters ?? {}) as PledgeFilters),
      }),
      transformResponse: (r: any) => ({
        data: r?.data ?? [],
        meta: r?.meta ?? {},
        pagination: r?.pagination ?? null,
      }),
      providesTags: [{ type: "GraduatePledgeSubmissions" as const, id: "LIST" }],
    }),

    getPledgeSubmission: b.query<PledgeSubmission, number>({
      query: (id) => `/admin/graduate-pledge/submissions/${id}`,
      transformResponse: (r: any) => pick(r),
      providesTags: (_r, _e, id) => [{ type: "GraduatePledgeSubmissions" as const, id }],
    }),

    /**
     * Save one record. Only the keys sent are written server-side, so a screen
     * may post a single tick-box without round-tripping — and without writing
     * back a stale copy of everything else.
     */
    updatePledgeSubmission: b.mutation<any, { id: number; body: Record<string, any> }>({
      query: ({ id, body }) => ({
        url: `/admin/graduate-pledge/submissions/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "GraduatePledgeSubmissions" as const, id: "LIST" },
        { type: "GraduatePledgeSubmissions" as const, id },
      ],
    }),

    deletePledgeSubmission: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/graduate-pledge/submissions/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "GraduatePledgeSubmissions" as const, id: "LIST" }],
    }),

    /**
     * Accept the signed printout, or send it back. A rejection needs a reason
     * and the API emails it to the student verbatim, so this is the one
     * mutation here whose text a stranger actually reads.
     */
    reviewPledgeSignedForm: b.mutation<
      any,
      { id: number; status: "accepted" | "rejected"; notes?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/graduate-pledge/submissions/${id}/signed/review`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "GraduatePledgeSubmissions" as const, id: "LIST" },
        { type: "GraduatePledgeSubmissions" as const, id },
      ],
    }),

    resendPledgeForm: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/graduate-pledge/submissions/${id}/resend`, method: "POST" }),
    }),

    /** The reminder before the cut-off, or the letter after results. */
    emailPledgeStudents: b.mutation<
      { message: string; data: { queued: number; failed: number } },
      { filters: PledgeFilters; subject: string; body: string; ids?: number[]; greet_by_name?: boolean }
    >({
      query: ({ filters, ...body }) => ({
        url: "/admin/graduate-pledge/submissions/email",
        method: "POST",
        // The filters ride in the query string, exactly as the list reads
        // them, so the audience is literally the list on screen.
        params: pledgeParams(filters),
        body,
      }),
    }),

    getPledgeCohorts: b.query<{ data: PledgeCohort[]; meta: any }, void>({
      query: () => "/admin/graduate-pledge/cohorts",
      transformResponse: (r: any) => ({ data: r?.data ?? [], meta: r?.meta ?? {} }),
      providesTags: [{ type: "GraduatePledgeCohorts" as const, id: "LIST" }],
    }),

    createPledgeCohort: b.mutation<any, Record<string, any>>({
      query: (body) => ({ url: "/admin/graduate-pledge/cohorts", method: "POST", body }),
      invalidatesTags: [
        { type: "GraduatePledgeCohorts" as const, id: "LIST" },
        { type: "GraduatePledgeSubmissions" as const, id: "LIST" },
      ],
    }),

    updatePledgeCohort: b.mutation<any, { id: number; body: Record<string, any> }>({
      query: ({ id, body }) => ({ url: `/admin/graduate-pledge/cohorts/${id}`, method: "POST", body }),
      invalidatesTags: [
        { type: "GraduatePledgeCohorts" as const, id: "LIST" },
        { type: "GraduatePledgeSubmissions" as const, id: "LIST" },
      ],
    }),

    setPledgeCohortStatus: b.mutation<any, { id: number; is_open: boolean }>({
      query: ({ id, is_open }) => ({
        url: `/admin/graduate-pledge/cohorts/${id}/status`,
        method: "POST",
        body: { is_open },
      }),
      invalidatesTags: [
        { type: "GraduatePledgeCohorts" as const, id: "LIST" },
        { type: "GraduatePledgeSubmissions" as const, id: "LIST" },
      ],
    }),

    deletePledgeCohort: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/graduate-pledge/cohorts/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "GraduatePledgeCohorts" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPledgeSubmissionsQuery,
  useGetPledgeSubmissionQuery,
  useUpdatePledgeSubmissionMutation,
  useDeletePledgeSubmissionMutation,
  useResendPledgeFormMutation,
  useReviewPledgeSignedFormMutation,
  useEmailPledgeStudentsMutation,
  useGetPledgeCohortsQuery,
  useCreatePledgeCohortMutation,
  useUpdatePledgeCohortMutation,
  useSetPledgeCohortStatusMutation,
  useDeletePledgeCohortMutation,
} = graduatePledgeApi;
